import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { CAMPO, LogoCfFbc, Recado } from "../componentes/ui";
import { mascararTelefone } from "../lib/mascaras";
import { rolarAte } from "../lib/rolarPara";
import {
  cadastrar,
  encaminharContato,
  enviar,
  lembrar,
  lerQuestionario,
  utmsDaBusca,
  type CadastroGuardado,
  type Questionario,
} from "../lib/diagnostico";

// O Diagnóstico de Implementação da Reforma Tributária, a página PÚBLICA (000373/000374).
//
// Era um app da Lovable (diagnostico.contabilidadefacilitada.com), e a decisão do Enzo de 29 e
// 30/10 foi trazê-lo para o hub como página pública, como o /orcamento e o /pesquisa: quem o faz
// não tem conta, e por isso a página não tem o Topo nem o Rodapé do hub.
//
// O caminho: o cadastro (nome, e-mail e WhatsApp) → as 25 perguntas, uma por vez → o relatório,
// em /diagnostico/relatorio/<token>. O que mudou em relação à Lovable, e por quê:
//
//   · a tela de entrada da Lovable pedia o E-MAIL e respondia se ele era de aluno, de convidado
//     ou desconhecido (`guest_exists`) — ou seja, dizia a qualquer um se um e-mail estava
//     cadastrado. Aqui não há essa pergunta: o cadastro devolve um token, e é ele que deixa enviar;
//   · os diagnósticos antigos eram reabertos pelo e-mail, e quem soubesse o e-mail de alguém lia o
//     diagnóstico dele. Aqui o relatório tem um link próprio, e a página guarda o último neste
//     navegador para a pessoa voltar a ele;
//   · a nota não é calculada aqui. A página manda as opções escolhidas, e o banco soma os pontos.
//
// As opções são BOTÕES, e não radios: o clique escolhe e passa para a próxima pergunta, como na
// Lovable. Com radios, as setas do teclado mudam a escolha e disparariam o avanço a cada tecla.
export default function Diagnostico() {
  const [busca] = useSearchParams();
  const [questionario, setQuestionario] = useState<Questionario | null | "carregando">("carregando");
  const [cadastro, setCadastro] = useState<CadastroGuardado | null>(() => lembrar.cadastro());
  const [comecou, setComecou] = useState<boolean>(() => lembrar.andamento() !== null);

  useEffect(() => {
    let vivo = true;
    void lerQuestionario().then((q) => {
      if (vivo) setQuestionario(q);
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (questionario === "carregando") return <Carregando texto="Abrindo o diagnóstico…" />;
  if (questionario === null) {
    return (
      <Moldura>
        <div className="surface p-7 text-center" data-situacao="sem-conexao">
          <h1 className="font-display text-xl font-bold text-titulo">Não conseguimos abrir o diagnóstico agora</h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cinza">
            Confira a conexão e recarregue a página.
          </p>
        </div>
      </Moldura>
    );
  }

  if (cadastro && comecou) {
    return (
      <Moldura>
        <Perguntas
          questionario={questionario}
          cadastro={cadastro}
          aoRecomecar={() => {
            lembrar.guardarAndamento(null);
            setComecou(false);
          }}
          aoPerderCadastro={() => {
            // As respostas ficam: depois do cadastro novo, a pessoa volta para onde estava.
            lembrar.guardarCadastro(null);
            setCadastro(null);
            setComecou(false);
          }}
        />
      </Moldura>
    );
  }

  return (
    <Moldura largo>
      <Entrada
        questionario={questionario}
        cadastro={cadastro}
        utm={utmsDaBusca(busca)}
        aoCadastrar={(c) => {
          lembrar.guardarCadastro(c);
          if (lembrar.andamento() === null) lembrar.guardarAndamento({});
          setCadastro(c);
          setComecou(true);
        }}
        aoComecar={() => {
          if (lembrar.andamento() === null) lembrar.guardarAndamento({});
          setComecou(true);
        }}
        aoTrocarDePessoa={() => {
          lembrar.guardarCadastro(null);
          lembrar.guardarAndamento(null);
          setCadastro(null);
        }}
      />
    </Moldura>
  );
}

function Entrada({
  questionario,
  cadastro,
  utm,
  aoCadastrar,
  aoComecar,
  aoTrocarDePessoa,
}: {
  questionario: Questionario;
  cadastro: CadastroGuardado | null;
  utm: Record<string, string>;
  aoCadastrar: (c: CadastroGuardado) => void;
  aoComecar: () => void;
  aoTrocarDePessoa: () => void;
}) {
  const ultimo = lembrar.ultimo();
  const temas = questionario.eixos.length;

  return (
    <>
      <section className="painel-navy rise rounded-3xl p-6 sm:p-10">
        <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
          <div>
            <p className="text-sm font-extrabold tracking-widest text-laranja uppercase">Diagnóstico da Reforma Tributária</p>
            <h1 className="mt-3 font-display text-2xl leading-tight font-extrabold text-white sm:text-4xl">
              A ferramenta que mede seu nível na <span className="text-laranja">Reforma Tributária</span> e o transforma
              em <span className="text-laranja">dinheiro</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-navy-mute">
              Faça o diagnóstico gratuito: são {questionario.perguntas.length} perguntas em {temas} temas, e no fim você
              recebe um relatório com o seu nível, os pontos de risco e um plano de ação de 30, 60 e 90 dias.
            </p>
          </div>
          {cadastro ? (
            <JaCadastrado cadastro={cadastro} aoComecar={aoComecar} aoTrocarDePessoa={aoTrocarDePessoa} />
          ) : (
            <FormularioDeCadastro utm={utm} aoCadastrar={aoCadastrar} />
          )}
        </div>
      </section>

      {ultimo && (
        <p className="mt-6 text-center text-sm text-cinza" data-ultimo-relatorio>
          Já fez o diagnóstico neste navegador?{" "}
          <Link to={`/diagnostico/relatorio/${ultimo}`} className="font-semibold text-link underline">
            Ver o seu último relatório
          </Link>
          .
        </p>
      )}
    </>
  );
}

function JaCadastrado({
  cadastro,
  aoComecar,
  aoTrocarDePessoa,
}: {
  cadastro: CadastroGuardado;
  aoComecar: () => void;
  aoTrocarDePessoa: () => void;
}) {
  const primeiro = cadastro.nome.split(/\s+/)[0] ?? "";
  return (
    <div className="surface p-7 text-center" data-ja-cadastrado>
      <p className="font-display text-lg font-bold text-titulo">Olá{primeiro ? `, ${primeiro}` : ""}!</p>
      <p className="mt-2 text-sm text-cinza">Seu cadastro já está feito neste navegador.</p>
      <button type="button" className="pill-cta mt-5 w-full" onClick={aoComecar} data-comecar>
        Começar o diagnóstico
      </button>
      <button type="button" className="mt-4 text-sm font-semibold text-link underline" onClick={aoTrocarDePessoa}>
        Não sou eu
      </button>
    </div>
  );
}

function FormularioDeCadastro({
  utm,
  aoCadastrar,
}: {
  utm: Record<string, string>;
  aoCadastrar: (c: CadastroGuardado) => void;
}) {
  const id = useId();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [enviando, setEnviando] = useState(false);
  // A recusa é do BANCO (`drt_cadastrar`), com o campo que ela aponta e a frase dela.
  const [recusa, setRecusa] = useState<{ motivo: string; mensagem: string } | null>(null);

  async function cadastrarAgora(e: React.FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setRecusa(null);
    const r = await cadastrar(nome, email, telefone, utm);
    setEnviando(false);
    if (!r.ok) {
      setRecusa({ motivo: r.motivo, mensagem: r.mensagem });
      return;
    }
    encaminharContato(r.token);
    aoCadastrar({ token: r.token, nome: nome.trim() });
  }

  const erroDe = (campo: string) =>
    recusa?.motivo === campo ? (
      <p id={`${id}-${campo}-erro`} className="mt-1 text-sm text-erro-tinta">
        {recusa.mensagem}
      </p>
    ) : null;

  return (
    <form className="surface space-y-4 p-6 sm:p-7" onSubmit={cadastrarAgora} noValidate data-cadastro>
      <h2 className="text-center font-display text-base font-bold tracking-wide text-titulo uppercase">Cadastre-se</h2>
      <div>
        <label htmlFor={`${id}-nome`} className="text-sm font-semibold text-tinta">
          Nome
        </label>
        <input
          id={`${id}-nome`}
          className={`${CAMPO} mt-1`}
          autoComplete="name"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          aria-invalid={recusa?.motivo === "nome"}
          aria-describedby={recusa?.motivo === "nome" ? `${id}-nome-erro` : undefined}
          data-campo="nome"
        />
        {erroDe("nome")}
      </div>
      <div>
        <label htmlFor={`${id}-email`} className="text-sm font-semibold text-tinta">
          E-mail
        </label>
        <input
          id={`${id}-email`}
          type="email"
          className={`${CAMPO} mt-1`}
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={recusa?.motivo === "email"}
          aria-describedby={recusa?.motivo === "email" ? `${id}-email-erro` : undefined}
          data-campo="email"
        />
        {erroDe("email")}
      </div>
      <div>
        <label htmlFor={`${id}-telefone`} className="text-sm font-semibold text-tinta">
          WhatsApp com DDD
        </label>
        <input
          id={`${id}-telefone`}
          type="tel"
          inputMode="tel"
          className={`${CAMPO} mt-1`}
          autoComplete="tel"
          value={telefone}
          onChange={(e) => setTelefone(mascararTelefone(e.target.value))}
          aria-invalid={recusa?.motivo === "telefone"}
          aria-describedby={recusa?.motivo === "telefone" ? `${id}-telefone-erro` : undefined}
          data-campo="telefone"
        />
        {erroDe("telefone")}
      </div>
      {recusa && !["nome", "email", "telefone"].includes(recusa.motivo) && <Recado tipo="erro" texto={recusa.mensagem} />}
      <button type="submit" className="pill-cta w-full" disabled={enviando} data-enviar-cadastro>
        {enviando ? "Cadastrando…" : "Quero meu relatório gratuito"}
      </button>
    </form>
  );
}

function Perguntas({
  questionario,
  cadastro,
  aoRecomecar,
  aoPerderCadastro,
}: {
  questionario: Questionario;
  cadastro: CadastroGuardado;
  aoRecomecar: () => void;
  aoPerderCadastro: () => void;
}) {
  const navegar = useNavigate();
  const { perguntas, eixos } = questionario;
  const [respostas, setRespostas] = useState<Record<string, string>>(() => lembrar.andamento() ?? {});
  // Volta à primeira pergunta sem resposta: quem recarregou a página continua de onde parou.
  const [atual, setAtual] = useState<number>(() => {
    const salvas = lembrar.andamento() ?? {};
    const i = perguntas.findIndex((q) => !(String(q.numero) in salvas));
    return i === -1 ? perguntas.length - 1 : i;
  });
  const [enviando, setEnviando] = useState(false);
  const [recusa, setRecusa] = useState<string | null>(null);
  const cartao = useRef<HTMLElement>(null);
  const avanco = useRef<number | null>(null);
  const idTitulo = useId();

  const pergunta = perguntas[atual];
  const eixoAtual = eixos.findIndex((e) => e.nome === pergunta.eixo);
  const respondidas = perguntas.filter((q) => String(q.numero) in respostas).length;
  const doEixo = useMemo(() => perguntas.filter((q) => q.eixo === pergunta.eixo), [perguntas, pergunta.eixo]);
  const ultima = atual === perguntas.length - 1;

  useEffect(() => () => {
    if (avanco.current !== null) window.clearTimeout(avanco.current);
  }, []);

  function irPara(i: number) {
    if (avanco.current !== null) window.clearTimeout(avanco.current);
    avanco.current = null;
    setAtual(i);
    rolarAte(cartao.current);
  }

  function escolher(valor: string) {
    const novas = { ...respostas, [String(pergunta.numero)]: valor };
    setRespostas(novas);
    lembrar.guardarAndamento(novas);
    setRecusa(null);
    if (!ultima) {
      // Um instante para a escolha aparecer marcada antes de a próxima pergunta entrar.
      if (avanco.current !== null) window.clearTimeout(avanco.current);
      avanco.current = window.setTimeout(() => irPara(atual + 1), 250);
    }
  }

  async function finalizar() {
    if (enviando) return;
    setEnviando(true);
    setRecusa(null);
    const r = await enviar(cadastro.token, respostas);
    setEnviando(false);
    if (!r.ok) {
      // O cadastro deste navegador não existe mais no banco: a pessoa se cadastra de novo, e as
      // respostas ficam guardadas para depois.
      if (r.motivo === "cadastro") aoPerderCadastro();
      else setRecusa(r.mensagem);
      return;
    }
    lembrar.guardarAndamento(null);
    lembrar.guardarUltimo(r.token);
    navegar(`/diagnostico/relatorio/${r.token}`, { replace: true });
  }

  return (
    <section ref={cartao} className="surface rise p-6 sm:p-8" role="group" aria-labelledby={idTitulo} data-questionario>
      <h1 className="text-center font-display text-xl font-bold text-titulo sm:text-2xl">
        Diagnóstico de Implementação da Reforma Tributária
      </h1>

      <ol className="mt-6 flex items-center justify-between gap-1" aria-label="Temas do diagnóstico">
        {eixos.map((e, i) => (
          <li
            key={e.nome}
            className={`flex size-9 items-center justify-center rounded-full text-sm ${
              i < eixoAtual
                ? "bg-acento text-acento-tinta"
                : i === eixoAtual
                  ? "bg-acento text-acento-tinta ring-4 ring-acento/25"
                  : "border-2 border-borda text-cinza"
            }`}
            title={e.nome}
            aria-current={i === eixoAtual ? "step" : undefined}
          >
            <span aria-hidden="true">{i < eixoAtual ? "✓" : e.emoji}</span>
            <span className="sr-only">
              {e.nome}
              {i < eixoAtual ? " (concluído)" : i === eixoAtual ? " (atual)" : ""}
            </span>
          </li>
        ))}
      </ol>

      <div className="mt-6">
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <p className="font-semibold text-tinta" data-eixo-atual>
            {eixos[eixoAtual]?.emoji} {pergunta.eixo}
            <span className="font-normal text-cinza">
              {" "}
              · {doEixo.findIndex((q) => q.numero === pergunta.numero) + 1} de {doEixo.length}
            </span>
          </p>
          <p className="text-cinza" data-progresso={`${atual + 1}/${perguntas.length}`}>
            Pergunta {atual + 1} de {perguntas.length}
          </p>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-borda" aria-hidden="true">
          <div className="h-full rounded-full bg-acento transition-all" style={{ width: `${((atual + 1) / perguntas.length) * 100}%` }} />
        </div>
      </div>

      <h2 id={idTitulo} className="mt-6 text-lg leading-snug font-semibold text-tinta" data-pergunta={pergunta.numero}>
        {pergunta.texto}
      </h2>
      <div className="mt-4 space-y-2.5" role="group" aria-labelledby={idTitulo}>
        {pergunta.opcoes.map((o) => {
          const escolhida = respostas[String(pergunta.numero)] === o.valor;
          return (
            <button
              key={o.valor}
              type="button"
              aria-pressed={escolhida}
              onClick={() => escolher(o.valor)}
              className={`w-full rounded-xl border-[1.5px] px-4 py-3.5 text-left text-base transition ${
                escolhida ? "border-acento bg-acento/10 font-semibold text-tinta" : "border-borda text-tinta hover:border-acento/60"
              }`}
              data-opcao={o.valor}
            >
              {o.rotulo}
            </button>
          );
        })}
      </div>

      {recusa && <Recado tipo="erro" texto={recusa} />}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className="btn-contorno min-h-10 text-sm"
          onClick={() => irPara(atual - 1)}
          disabled={atual === 0}
          data-anterior
        >
          Anterior
        </button>
        {ultima ? (
          <button
            type="button"
            // Travado é estado PERMANENTE até a pessoa voltar e responder, e a frase "Faltam N" diz
            // por quê. Nem a opacidade (reprova contraste) nem a `pill-desligada`, que é desenhada
            // para o território navy: o texto dela é `navy-mute`, e sobre este cartão claro ele some.
            className={respondidas < perguntas.length ? "btn-contorno min-h-10 cursor-not-allowed text-sm" : "pill-cta"}
            onClick={() => void finalizar()}
            disabled={enviando || respondidas < perguntas.length}
            data-finalizar
          >
            {enviando ? "Calculando…" : "Ver meu diagnóstico"}
          </button>
        ) : (
          String(pergunta.numero) in respostas && (
            <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => irPara(atual + 1)} data-proxima>
              Próxima
            </button>
          )
        )}
      </div>
      {ultima && respondidas < perguntas.length && (
        <p className="mt-3 text-sm text-cinza" data-faltam>
          Faltam {perguntas.length - respondidas === 1 ? "1 resposta" : `${perguntas.length - respondidas} respostas`}.
          Volte pelas perguntas anteriores para responder.
        </p>
      )}
      <p className="mt-6 text-center text-xs text-cinza">
        <button type="button" className="underline" onClick={aoRecomecar} data-recomecar>
          Recomeçar do zero
        </button>
      </p>
    </section>
  );
}

/** A moldura das páginas públicas (a do /pesquisa): a marca no alto, e nada do hub do aluno. */
export function Moldura({ children, largo = false }: { children: React.ReactNode; largo?: boolean }) {
  return (
    <div className="flex min-h-dvh flex-col bg-fundo">
      <header className="bg-azul no-imprimir">
        <div className={`mx-auto flex ${largo ? "max-w-6xl" : "max-w-3xl"} items-center px-4 py-4 sm:px-6`}>
          <LogoCfFbc className="h-8" />
        </div>
      </header>
      <main className={`mx-auto w-full ${largo ? "max-w-6xl" : "max-w-3xl"} flex-1 px-4 py-8 sm:px-6`}>{children}</main>
      <footer className="bg-azul py-5 text-center no-imprimir">
        <p className="text-xs text-navy-mute">© {new Date().getFullYear()} Contabilidade Facilitada</p>
      </footer>
    </div>
  );
}
